import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, BehaviorSubject } from 'rxjs';

import { Language } from '@shared/models';

import { AppConstants } from '../constants/app.consts';

import { BaseService } from './base.service';
import { LocalStorageService } from './local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class LocalizationService extends BaseService {
  private availableLanguages: Array<Language> = [];
  private currentLang?: string;

  availableLanguagesObs = new BehaviorSubject<Array<Language>>(this.availableLanguages);

  constructor(
    private localStorageService: LocalStorageService,
    private http: HttpClient,
    private translate: TranslateService
  ) {
    super();
  }

  init() {
    this.translate.setDefaultLang(AppConstants.DefaultLanguage);
    this.translate.use(this.getCurrentLanguage());
    this.translate.onLangChange.subscribe((x) => this.setLanguage(x.lang));
    this.getAllPublishedLanguages();
  }

  setLanguage(lang: string) {
    this.translate.use(lang);
    this.currentLang = lang;
    this.localStorageService.store(LocalStorageService.PREFERENCES_LANGUAGE, lang);
  }

  getCurrentLanguage(): string {
    if (!this.currentLang) {
      this.currentLang = this.localStorageService.load(LocalStorageService.PREFERENCES_LANGUAGE);
      if (!this.currentLang || this.currentLang === 'keys') {
        this.setLanguage(AppConstants.DefaultLanguage);
      }
    }
    return this.currentLang!;
  }

  getAllLanguages(): Observable<Array<Language>> {
    return this.availableLanguagesObs.asObservable();
  }

  getAllPublishedLanguages() {
    this.http.get<Array<Language>>('./assets/i18n/languages.json').subscribe((x) => {
      this.availableLanguages = x;
      this.availableLanguagesObs.next(this.availableLanguages);
      this.translate.addLangs(x.map((l) => l.culture));
    });
  }

  showTranslationsKeys() {
    const translation = this.translate.translations[this.translate.currentLang];
    const keysTranslations = this.mapTranslations(translation);

    this.translate.addLangs(['keys']);
    this.translate.setTranslation('keys', keysTranslations);
    setTimeout(() => this.translate.use('keys'), 0);
  }

  private mapTranslations(translation: unknown, parentKey: string = undefined) {
    const result = Object.assign({}, translation);
    const keys = Object.keys(result);

    keys.forEach((x) => {
      if (result[x] instanceof Object) {
        result[x] = this.mapTranslations(result[x], parentKey ? `${parentKey}.${x}` : x);
      } else {
        result[x] = parentKey ? `${parentKey}.${x}` : x;
      }
    });

    return result;
  }
}
